"use client";

import { HStack, Stack, Text, Tooltip } from "@leabankab/component-library";
import { SUBMIT_ERROR_PROPS } from "@/components/form";
import type { AllocationMarkerId } from "@/features/refinance/utils/allocation-markers";
import type { RefinanceAllocation } from "@/features/refinance/utils/refinance-allocation";

/**
 * The bar reads as up to three regions: a top-up's existing Lea Bank loan, what
 * the loan requires the customer to redeem, and everything they are free to
 * gather on top of it. The markers carry the distinctions within the last.
 */
type RegionTone = "existing" | "required" | "gathered";

export interface AllocationLegendLabels {
	existing: string;
	mandatory: string;
	gathered: string;
}

export interface AllocationBarMarker {
	id: AllocationMarkerId;
	/** Position along the track, as a fraction of the whole. */
	ratio: number;
	/** Serves as both the tooltip body and the trigger's accessible name. */
	description: string;
}

interface DebtAllocationBarProps {
	/** Row heading, also the bar's accessible name. */
	label: string;
	/** The running total shown next to the heading. */
	amount: string;
	/** Spoken description of the whole allocation. */
	valueText: string;
	caption?: string;
	/** The caption explains why the last submit was blocked. */
	captionBlocksSubmit?: boolean;
	allocation: RefinanceAllocation;
	markers: AllocationBarMarker[];
	/** Tooltip body and accessible name for the existing loan's region. */
	existingDescription: string;
	/** Tooltip body and accessible name for the required region. */
	mandatoryDescription: string;
	markerGroupLabel: string;
	legendLabels: AllocationLegendLabels;
}

/** Diagonal stripes marking purple the loan carries but the customer has yet to cover. */
const PENDING_HATCH =
	"repeating-linear-gradient(45deg, var(--lea-color-purple-100) 0 3px, transparent 3px 12px)";

/** The saturated tone each region fills with, and what the legend swatches show. */
const REGION_BG: Record<RegionTone, string> = {
	existing: "bg-neutral-500",
	required: "bg-purple-600",
	gathered: "bg-teal-300",
};

const MARKER_HIT_AREA = 24;
/** Caps whichever segment the fill ends on; the track clips the outer ends. */
const ROUNDED_END = "0 999px 999px 0";
const FOCUS_RING =
	"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-base";

// The pale gathered region barely registers against the white card, so the track is outlined.
const TRACK_CLASSES =
	"relative h-1.5 w-full overflow-hidden rounded-full border border-border-subtle bg-teal-200";

const percent = (ratio: number) => `${ratio * 100}%`;

const LegendSwatch = ({ tone }: { tone: RegionTone }) => (
	<span
		aria-hidden="true"
		className={`size-1.5 shrink-0 rounded-sm border border-border-base ${REGION_BG[tone]}`}
	/>
);

export const DebtAllocationBar = ({
	label,
	amount,
	valueText,
	caption,
	captionBlocksSubmit = false,
	allocation,
	markers,
	existingDescription,
	mandatoryDescription,
	markerGroupLabel,
	legendLabels,
}: DebtAllocationBarProps) => {
	const { internal, mandatory, collected, maxLoanAmount } = allocation;

	if (maxLoanAmount <= 0) return null;

	const existingRatio = Math.min(internal / maxLoanAmount, 1);
	const requiredRatio = Math.min(
		mandatory.capacity / maxLoanAmount,
		1 - existingRatio
	);
	const settledRatio = existingRatio + requiredRatio;
	// The required redemption is part of the loan whether or not the customer
	// has documented it, so its purple is drawn whole from the start and only
	// what they gather beyond it reads as progress.
	const gatheredRatio = Math.max(
		Math.min(collected / maxLoanAmount, 1) - settledRatio,
		0
	);
	const hasGatheredRegion = settledRatio < 1;
	// Grey and the next region's fill are too close to tell apart on hue alone.
	const existingIsFollowed = requiredRatio > 0 || gatheredRatio > 0;

	return (
		<Stack gap={1}>
			<HStack justify="between">
				<Text color="text.base">{label}</Text>
				<Text textStyle="body-sm" fontWeight="medium">
					{amount}
				</Text>
			</HStack>

			<div
				className="relative flex items-center"
				style={{ height: MARKER_HIT_AREA }}
			>
				<div
					role="progressbar"
					aria-label={label}
					aria-valuemin={0}
					aria-valuemax={maxLoanAmount}
					aria-valuenow={Math.min(collected, maxLoanAmount)}
					aria-valuetext={valueText}
					className={TRACK_CLASSES}
				>
					{existingRatio > 0 && (
						<div
							className={`absolute inset-y-0 start-0 box-border ${REGION_BG.existing} ${
								existingIsFollowed ? "border-e-2 border-bg-base" : ""
							}`}
							style={{
								width: percent(existingRatio),
								borderRadius: existingIsFollowed ? 0 : ROUNDED_END,
							}}
						/>
					)}
					{requiredRatio > 0 && (
						<div
							className={`absolute inset-y-0 ${REGION_BG.required}`}
							style={{
								insetInlineStart: percent(existingRatio),
								width: percent(requiredRatio),
								borderRadius: gatheredRatio > 0 ? 0 : ROUNDED_END,
							}}
						/>
					)}
					{gatheredRatio > 0 && (
						<div
							className={`absolute inset-y-0 ${REGION_BG.gathered}`}
							style={{
								insetInlineStart: percent(settledRatio),
								width: percent(gatheredRatio),
								borderRadius: ROUNDED_END,
							}}
						/>
					)}
					{mandatory.remaining > 0 && (
						<div
							className="absolute inset-y-0"
							style={{
								insetInlineStart: percent(collected / maxLoanAmount),
								width: percent(mandatory.remaining / maxLoanAmount),
								backgroundImage: PENDING_HATCH,
							}}
						/>
					)}
				</div>

				<div
					role="group"
					aria-label={markerGroupLabel}
					className="absolute inset-0"
				>
					{existingRatio > 0 && (
						<Tooltip title={existingDescription} openOn="click">
							<button
								type="button"
								aria-label={existingDescription}
								className={`absolute inset-y-0 start-0 cursor-pointer rounded-full ${FOCUS_RING}`}
								style={{ width: percent(existingRatio) }}
							/>
						</Tooltip>
					)}

					{requiredRatio > 0 && (
						<Tooltip title={mandatoryDescription} openOn="click">
							<button
								type="button"
								aria-label={mandatoryDescription}
								className={`absolute inset-y-0 cursor-pointer rounded-full ${FOCUS_RING}`}
								style={{
									insetInlineStart: percent(existingRatio),
									width: percent(requiredRatio),
								}}
							/>
						</Tooltip>
					)}

					{markers.map((marker) => (
						<Tooltip key={marker.id} title={marker.description} openOn="click">
							<button
								type="button"
								aria-label={marker.description}
								className="absolute top-1/2 z-1 flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-text-base"
								style={{
									insetInlineStart: percent(marker.ratio),
									width: MARKER_HIT_AREA,
									height: MARKER_HIT_AREA,
								}}
							>
								<span
									aria-hidden="true"
									className="size-1.5 rounded-full border-2 border-teal-600 bg-bg-base"
								/>
							</button>
						</Tooltip>
					))}
				</div>
			</div>

			<HStack render={<ul />} wrap gap={1.5} className="m-0 list-none p-0">
				{existingRatio > 0 && (
					<HStack render={<li />} align="center" gap={0.5}>
						<LegendSwatch tone="existing" />
						<Text textStyle="body-sm" color="text.base">
							{legendLabels.existing}
						</Text>
					</HStack>
				)}
				{requiredRatio > 0 && (
					<HStack render={<li />} align="center" gap={0.5}>
						<LegendSwatch tone="required" />
						<Text textStyle="body-sm" color="text.base">
							{legendLabels.mandatory}
						</Text>
					</HStack>
				)}
				{hasGatheredRegion && (
					<HStack render={<li />} align="center" gap={0.5}>
						<LegendSwatch tone="gathered" />
						<Text textStyle="body-sm" color="text.base">
							{legendLabels.gathered}
						</Text>
					</HStack>
				)}
			</HStack>

			{caption && (
				<Text
					{...(captionBlocksSubmit ? SUBMIT_ERROR_PROPS : {})}
					textStyle="body-sm"
					color={captionBlocksSubmit ? "status.error.base" : "text.base"}
					aria-live="polite"
					aria-atomic="true"
				>
					{caption}
				</Text>
			)}
		</Stack>
	);
};
